import {
  type TApproveProposalPayload,
  type TCreateProposalCommentPayload,
  type TCreateProposalPayload,
  type TCreateProposalResponse,
  type TCreateProposalTypePayload,
  type TDeleteProposalCommentPayload,
  type TGetCurrentUserProposalsQueryParams,
  type TGetCurrentUserProposalsQueryResponse,
  type TGetProposalTypesQueryParams,
  type TProposalDetail,
  type TProposalTypeListItem,
  type TUpdateProposalFollowersPayload,
  type TUpdateProposalPayload,
  type TUpdateProposalTypePayload,
} from 'features/proposal/types';
import { baseApi } from 'lib/rtkQuery/baseApi';
import { QueryTags } from 'lib/rtkQuery/constants';
import { invalidatesTags } from 'lib/rtkQuery/helpers';
import { type ListQueryResponse } from 'lib/rtkQuery/types';
import { type TCommentListItem } from 'shared/components/CommentsWidget/types';

const endpoint = `/proposals`;

export const proposalsApi = baseApi
  .enhanceEndpoints({
    addTagTypes: [
      QueryTags.ProposalComment,
      QueryTags.CurrentUserProposals,
      QueryTags.ProposalType,
      QueryTags.ProposalDetail,
    ],
  })
  .injectEndpoints({
    endpoints: builder => ({
      GetCurrentUserProposals: builder.query<
        TGetCurrentUserProposalsQueryResponse,
        TGetCurrentUserProposalsQueryParams | void
      >({
        query: params => ({
          url: `${endpoint}/current-user`,
          params,
        }),
        providesTags: [QueryTags.CurrentUserProposals],
      }),

      GetAllProposalTypes: builder.query<
        ListQueryResponse<TProposalTypeListItem[]>,
        TGetProposalTypesQueryParams | void
      >({
        query: params => ({
          url: `${endpoint}/type`,
          ...(params && { params }),
        }),
        providesTags: [QueryTags.ProposalType],
      }),

      CreateProposal: builder.mutation<
        TCreateProposalResponse,
        TCreateProposalPayload
      >({
        query: data => ({
          url: `${endpoint}/others`,
          method: 'POST',
          data,
        }),
        invalidatesTags: invalidatesTags([QueryTags.CurrentUserProposals]),
      }),

      UpdateProposal: builder.mutation<void, TUpdateProposalPayload>({
        query: data => ({
          url: `${endpoint}/others`,
          method: 'PUT',
          data,
        }),
        invalidatesTags: invalidatesTags([
          QueryTags.CurrentUserProposals,
          QueryTags.ProposalDetail,
        ]),
      }),

      GetProposalDetail: builder.query<TProposalDetail, string>({
        query: proposalCode => ({
          url: `${endpoint}/others/${proposalCode}`,
        }),
        providesTags: [QueryTags.ProposalDetail],
      }),

      GetProposalComments: builder.query<TCommentListItem[], string>({
        query: proposalCode => ({
          url: `${endpoint}/others/${proposalCode}/comments`,
        }),
        providesTags: [QueryTags.ProposalComment],
      }),

      DeleteProposalComment: builder.mutation<
        void,
        TDeleteProposalCommentPayload
      >({
        query: ({ proposalCode, commentId }) => ({
          url: `${endpoint}/others/${proposalCode}/comments/${commentId}`,
          method: 'DELETE',
        }),
        invalidatesTags: invalidatesTags([QueryTags.ProposalComment]),
      }),

      CreateProposalComment: builder.mutation<
        void,
        TCreateProposalCommentPayload
      >({
        query: ({ proposalCode, data }) => ({
          url: `${endpoint}/others/${proposalCode}/comments`,
          method: 'POST',
          data,
        }),
        invalidatesTags: invalidatesTags([QueryTags.ProposalComment]),
      }),

      UpdateProposalFollowers: builder.mutation<
        void,
        TUpdateProposalFollowersPayload
      >({
        query: data => ({
          url: `${endpoint}/others/followers`,
          method: 'PUT',
          data,
        }),
        invalidatesTags: invalidatesTags([QueryTags.ProposalDetail]),
      }),

      CreateProposalType: builder.mutation<
        TProposalTypeListItem,
        TCreateProposalTypePayload
      >({
        query: data => ({
          url: `${endpoint}/type`,
          method: 'POST',
          data,
        }),
        invalidatesTags: invalidatesTags([QueryTags.ProposalType]),
      }),

      UpdateProposalType: builder.mutation<
        TProposalTypeListItem,
        TUpdateProposalTypePayload
      >({
        query: ({ id, ...data }) => ({
          url: `${endpoint}/type/${id}`,
          method: 'PUT',
          data,
        }),
        invalidatesTags: invalidatesTags([QueryTags.ProposalType]),
      }),

      DeleteProposalType: builder.mutation<void, string>({
        query: id => ({
          url: `${endpoint}/type/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: invalidatesTags([QueryTags.ProposalType]),
      }),

      DeleteProposal: builder.mutation<void, string>({
        query: id => ({
          url: `${endpoint}/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: invalidatesTags([QueryTags.CurrentUserProposals]),
      }),

      ApproveProposal: builder.mutation<void, TApproveProposalPayload>({
        query: ({ proposalCode, ...data }) => ({
          url: `${endpoint}/${proposalCode}/approve`,
          method: 'PUT',
          data,
        }),
        invalidatesTags: invalidatesTags([
          QueryTags.ProposalDetail,
          QueryTags.CurrentUserProposals,
        ]),
      }),
    }),
  });

export const {
  useGetCurrentUserProposalsQuery,
  useGetAllProposalTypesQuery,
  useCreateProposalTypeMutation,
  useUpdateProposalTypeMutation,
  useDeleteProposalTypeMutation,
  useGetProposalDetailQuery,
  useGetProposalCommentsQuery,
  useCreateProposalMutation,
  useUpdateProposalMutation,
  useDeleteProposalCommentMutation,
  useCreateProposalCommentMutation,
  useUpdateProposalFollowersMutation,
  useDeleteProposalMutation,
  useApproveProposalMutation,
} = proposalsApi;
